import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { HiArrowLeft, HiMail } from "react-icons/hi";
import { IResolveParams } from "reactjs-social-login";
import SocialAuth from "../../../../routes/auth/SocialAuth";
import { CustomButton } from "../../../atoms/CustomButton";
import { EmailVerificationModal } from "./EmailVerificationModal";
import { CustomInput } from "../../../atoms/CustomInput";
import { EMAIL_PATTERN_SIMPLE } from "../../../../utils/validationPatterns";
import { useUserForgotPassword } from "../../../../hooks/users";
import { usePasswordResetToasts } from "../../../../hooks/usePasswordResetToasts";
import {
  KNOWN_PASSWORD_LEVELS,
  meetsPasswordRules,
  usePasswordRules,
} from "../../../../hooks/usePasswordResetContext";

interface EmailFormData {
  email: string;
  password?: string;
}

interface Step1AuthMethodProps {
  onEmailClick: () => void;
  onLoginStart: () => void;
  onResolve: (
    provider: IResolveParams["provider"],
    data: IResolveParams["data"]
  ) => void;
  onReject: (err: any) => void;
  isDisabled?: boolean;
  onBack?: () => void;
  requirePassword?: boolean;
  entityIdentification?: string;
  onEmailRegister?: (
    data: { email: string; password?: string },
    onEmailVerificationNeeded: (userId: string) => void,
    onAlreadyExists?: (email: string, password?: string) => void,
    explainRefusal?: () => boolean
  ) => void;
  onEmailLogin?: (email: string, password: string) => void;
  onExistingUser?: () => void;
}

export const Step1AuthMethod: React.FC<Step1AuthMethodProps> = ({
  onEmailClick,
  onLoginStart,
  onResolve,
  onReject,
  isDisabled = false,
  onBack,
  requirePassword = false,
  entityIdentification = "",
  onEmailRegister,
  onEmailLogin,
  onExistingUser,
}) => {
  const { t } = useTranslation("clientView");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingClientEmail, setExistingClientEmail] = useState("");
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [userId, setUserId] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [savedPassword, setSavedPassword] = useState("");
  const [showLoginMode, setShowLoginMode] = useState(false);
  const [loginModeEmail, setLoginModeEmail] = useState("");

  const { mutate: forgotPassword, isLoading: isSendingReset } =
    useUserForgotPassword({ notify: false });
  const resetToasts = usePasswordResetToasts();

  const { passwordLevel, passwordRules, acceptedLevels } = usePasswordRules(
    requirePassword && entityIdentification ? entityIdentification : null
  );
  const passwordRequirement =
    passwordLevel && KNOWN_PASSWORD_LEVELS.has(passwordLevel)
      ? (t(`passwordLevels.${passwordLevel}`) as string)
      : undefined;
  const followsPasswordRules = (password?: string) =>
    meetsPasswordRules(
      password ?? "",
      { level: passwordLevel ?? "", rules: passwordRules },
      acceptedLevels
    );

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<EmailFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const {
    control: loginControl,
    handleSubmit: handleLoginFormSubmit,
    formState: { errors: loginErrors },
  } = useForm<EmailFormData>({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleEmailButtonClick = () => {
    const shouldShowForm =
      typeof onEmailRegister === "function" &&
      typeof onEmailLogin === "function";

    if (shouldShowForm) {
      setShowEmailForm(true);
    } else {
      onEmailClick();
    }
  };

  const getRandomElement = (arr: any) => {
    const rand = Math.floor(Math.random() * arr.length);
    return arr[rand];
  };

  const generateRandomPasswordSelection = (length: number) => {
    const uppercase = [
      "A",
      "B",
      "C",
      "D",
      "E",
      "F",
      "G",
      "H",
      "I",
      "J",
      "K",
      "L",
      "M",
      "N",
      "O",
      "P",
      "Q",
      "R",
      "S",
      "T",
      "U",
      "V",
      "W",
      "X",
      "Y",
      "Z",
    ];
    const lowercase = [
      "a",
      "b",
      "c",
      "d",
      "e",
      "f",
      "g",
      "h",
      "i",
      "j",
      "k",
      "l",
      "m",
      "n",
      "o",
      "p",
      "q",
      "r",
      "s",
      "t",
      "u",
      "v",
      "w",
      "x",
      "y",
      "z",
    ];
    const special = ["!", "@", "&"];
    const numbers = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9"];

    const nonSpecial = [...uppercase, ...lowercase, ...numbers];

    let password = "";

    for (let i = 0; i < length; i++) {
      if (i === 0) {
        password += getRandomElement(uppercase);
      } else if (i === 1) {
        password += getRandomElement(lowercase);
      } else if (i === 2) {
        password += getRandomElement(numbers);
      } else if (i === 3) {
        password += getRandomElement(special);
      } else if (special.includes(password[i - 1])) {
        password += getRandomElement(nonSpecial).trim();
      } else password += getRandomElement([...nonSpecial, ...special]).trim();
    }

    return password;
  };

  function generateRandomPasswordLevelSix(): string {
    const passwordRegex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*#?&_.,+=:;<>€£()/\\-])[A-Za-z\d@$!%*#?&_.,+=:;<>€£()/\\-]{10,32}$/;
    const randomPassword = generateRandomPasswordSelection(12);
    return passwordRegex.test(randomPassword)
      ? randomPassword
      : generateRandomPasswordLevelSix();
  }

  const forgotPasswordLink = (email: string) => (
    <div className="text-end">
      <button
        type="button"
        disabled={isSendingReset}
        onClick={() =>
          forgotPassword(
            { entityIdentification, user_email: email },
            { onSuccess: resetToasts.sent, onError: resetToasts.refused }
          )
        }
        className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
      >
        {t("customRegistration.page.step1.forgotPasswordLink")}
      </button>
    </div>
  );

  const handleAlreadyExists = (email: string, password?: string) => {
    setIsSubmitting(false);
    onExistingUser?.();
    if (password && onEmailLogin) {
      setExistingClientEmail(email);
      onEmailLogin(email, password);
    } else {
      setLoginModeEmail(email);
      setShowLoginMode(true);
    }
  };

  const onSubmit = async (data: EmailFormData) => {
    if (!requirePassword) {
      data.password = generateRandomPasswordLevelSix();
    }
    setIsSubmitting(true);
    setSavedEmail(data.email);
    setSavedPassword(data.password as string);

    if (onEmailRegister) {
      await onEmailRegister(
        { email: data.email, password: data.password },
        (newUserId: string) => {
          setUserId(newUserId);
          setShowVerificationModal(true);
          setIsSubmitting(false);
        },
        handleAlreadyExists,
        () => {
          if (!requirePassword || followsPasswordRules(data.password)) {
            return false;
          }
          setError("password", {
            type: "server",
            message:
              passwordRequirement ??
              (t("customRegistration.page.toast.checkDetails") as string),
          });
          return true;
        }
      );
      setIsSubmitting(false);
    }
  };

  const onLoginModeSubmit = (data: EmailFormData) => {
    if (onEmailLogin) {
      onEmailLogin(loginModeEmail, data.password as string);
    }
  };

  const handleVerifyEmail = () => {
    if (onEmailLogin) {
      onEmailLogin(savedEmail, savedPassword);
    }
  };

  const handleBackFromForm = () => {
    setShowEmailForm(false);
    reset();
    setSavedEmail("");
    setSavedPassword("");
  };

  if (showLoginMode) {
    return (
      <div className="space-y-6">
        <CustomButton
          variant="none"
          size="xs"
          onClick={() => setShowLoginMode(false)}
          icon={<HiArrowLeft size={16} className="rtl:rotate-180" />}
          title={t("customRegistration.page.step3.backButton")}
          className="mb-4 justify-start px-0 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        />

        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("customRegistration.page.step1.existingAccountTitle")}
          </h2>
          <p className="text-gray-600 text-sm mb-1">
            {t("customRegistration.page.step1.existingAccountSubtitle")}
          </p>
          <p className="text-gray-500 text-sm font-medium">{loginModeEmail}</p>
        </div>

        <form
          onSubmit={handleLoginFormSubmit(onLoginModeSubmit)}
          className="space-y-4"
        >
          <CustomInput name="email" control={control} type="hidden" />

          <CustomInput
            name="password"
            control={loginControl}
            label={`${t("register.form.password")} *`}
            placeholder={
              t(
                "customRegistration.page.step1.loginPasswordPlaceholder"
              ) as string
            }
            type="password"
            autoComplete="current-password"
            error={loginErrors.password}
            helperText={loginErrors.password?.message}
            rules={{
              required: t("register.form.passwordRequired") as string,
            }}
          />
          {forgotPasswordLink(loginModeEmail)}
          <CustomButton
            variant="primary"
            type="submit"
            title={t("customRegistration.page.step1.loginButton") as string}
            className="w-full"
          />
        </form>
      </div>
    );
  }

  if (showEmailForm) {
    return (
      <div className="space-y-6">
        <CustomButton
          variant="none"
          size="xs"
          onClick={handleBackFromForm}
          icon={<HiArrowLeft size={16} className="rtl:rotate-180" />}
          title={t("customRegistration.page.step3.backButton")}
          className="mb-4 justify-start px-0 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        />

        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {t("customRegistration.page.step1.emailFormTitle")}
          </h2>
          <p className="text-gray-600 text-sm">
            {t("customRegistration.page.step1.emailFormSubtitle")}
          </p>
        </div>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-4">
          <CustomInput
            name="email"
            control={control}
            label={`${t("register.form.email")} *`}
            placeholder={t("register.form.emailPlaceholder") as string}
            type="email"
            error={errors.email}
            helperText={errors.email?.message}
            disabled={isSubmitting}
            rules={{
              required: t("register.form.emailRequired") as string,
              pattern: {
                value: EMAIL_PATTERN_SIMPLE,
                message: t("register.form.emailInvalid") as string,
              },
            }}
          />

          {requirePassword && (
            <CustomInput
              name="password"
              control={control}
              label={`${t("register.form.password")} *`}
              placeholder={t("register.form.passwordPlaceholder") as string}
              type="password"
              autoComplete="current-password"
              error={errors.password}
              helperText={errors.password?.message}
              hint={
                !errors.password && passwordRequirement ? (
                  <p dir="auto" className="text-xs text-gray-500">
                    {passwordRequirement}
                  </p>
                ) : undefined
              }
              disabled={isSubmitting}
              rules={{
                required: t("register.form.passwordRequired") as string,
              }}
            />
          )}
          {requirePassword &&
            existingClientEmail &&
            watch("email") === existingClientEmail &&
            forgotPasswordLink(existingClientEmail)}

          <CustomButton
            variant="primary"
            type="submit"
            title={t("customRegistration.page.step1.continueButton") as string}
            isLoading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          />
        </form>

        <EmailVerificationModal
          onClose={() => setShowVerificationModal(false)}
          show={showVerificationModal}
          email={savedEmail}
          password={savedPassword}
          userId={userId}
          entityIdentification={entityIdentification}
          onVerifyClick={handleVerifyEmail}
          isVerifying={false}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {onBack && (
        <CustomButton
          variant="none"
          size="xs"
          onClick={onBack}
          icon={<HiArrowLeft size={16} className="rtl:rotate-180" />}
          title={t("customRegistration.page.step3.backButton")}
          className="mb-4 justify-start px-0 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
        />
      )}

      <div className="text-center mb-6">
        <h2 className="text-base font-semibold text-gray-900 mb-2">
          {t("customRegistration.page.step1.title")}
        </h2>
      </div>

      <div className="space-y-3">
        <SocialAuth
          className="w-full"
          providers={["Google", "Apple"]}
          onLoginStartSocialAuthStart={onLoginStart}
          onResolveSocialAuth={onResolve}
          onRejectSocialAuth={onReject}
          providersIcon={{
            Google: (
              <CustomButton
                type="button"
                disabled={isDisabled}
                variant="secondary"
                className="w-full"
                comp={
                  <div className="flex items-center justify-center gap-3">
                    <img
                      src="../../images/google.svg"
                      alt=""
                      className="w-6 h-6"
                    />
                    <span className="text-gray-700 font-medium text-base">
                      {t("customRegistration.page.step1.continueWithGoogle")}
                    </span>
                  </div>
                }
              />
            ),
            Apple: (
              <CustomButton
                type="button"
                disabled={isDisabled}
                variant="secondary"
                className="w-full"
                comp={
                  <div className="flex items-center justify-center gap-3">
                    <img
                      src="../../images/apple-black.svg"
                      alt=""
                      className="w-6 h-6"
                    />
                    <span className="text-gray-700 font-medium text-base">
                      {t("customRegistration.page.step1.continueWithApple")}
                    </span>
                  </div>
                }
              />
            ),
          }}
          redirectUrl={{
            Apple: `${process.env.REACT_APP_APPLE_CALLBACK}`,
          }}
        />

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-white text-gray-500">
              {t("customRegistration.page.step1.divider")}
            </span>
          </div>
        </div>

        <CustomButton
          type="button"
          onClick={handleEmailButtonClick}
          disabled={isDisabled}
          variant="secondary"
          className="w-full"
          comp={
            <div className="flex items-center justify-center gap-3">
              <HiMail className="text-blue-600" size={24} />
              <span className="text-gray-700 font-medium text-base">
                {t("customRegistration.page.step1.continueWithEmail")}
              </span>
            </div>
          }
        />
      </div>
    </div>
  );
};
